import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { makeRequest2 } from '../../../../../utils/makeRequest';
import { toastify } from '../../../../../utils/toast';
import { useFacilityStore } from '../../../../../app/z-store/store';
import {
  addCommonAreaWaterReadingUrl,
  updateCommonAreaWaterReadingURL,
  getCommonAreaWaterReadingsUrl
} from '../../../../../utils/urls';

const WaterTab = () => {
  const [waterData, setWaterData] = useState([]);
  const [allWaterData, setAllWaterData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [waterDialogVisible, setWaterDialogVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedWaterItem, setSelectedWaterItem] = useState(null);
  const [waterForm, setWaterForm] = useState({
    sourceType: 'City Council',
    openingReadingM3: '',
    closingReadingM3: '',
    consumptionM3: '',
    date: new Date().toISOString().split('T')[0]
  });

  const waterSourceOptions = [
    { label: 'City Council', value: 'City Council' },
    { label: 'Borehole', value: 'Borehole' }
  ];

  const facilityId = useFacilityStore((state) => state.facilityId);

  useEffect(() => {
    if (facilityId) {
      fetchWaterData();
    }
  }, [facilityId]);

  const fetchWaterData = async () => {
    if (!facilityId) return;

    setLoading(true);
    try {
      const response = await makeRequest2(
        `${getCommonAreaWaterReadingsUrl}/${facilityId}`,
        'GET'
      );

      if (
        response.success &&
        response.data &&
        response.data.readings
      ) {
        const readings = response.data.readings;
        const data = Array.isArray(readings) ? readings : [readings];
        setAllWaterData(data);
        setWaterData(data);
      }
    } catch (error) {
      toastify("Failed to load water data", "error");
    } finally {
      setLoading(false);
    }
  };

  const calculateWaterConsumption = (opening, closing, setter, form) => {
    if (opening !== '' && closing !== '') {
      const openingVal = parseFloat(opening);
      const closingVal = parseFloat(closing);

      if (!isNaN(openingVal) && !isNaN(closingVal) && closingVal >= openingVal) {
        const consumption = closingVal - openingVal;
        setter({
          ...form,
          consumptionM3: consumption.toFixed(2)
        });
      }
    }
  };

  const resetWaterForm = () => {
    setWaterForm({
      sourceType: 'City Council',
      openingReadingM3: '',
      closingReadingM3: '',
      consumptionM3: '',
      date: new Date().toISOString().split('T')[0]
    });
  };

  const filterDataByDateRange = () => {
    if (!startDate && !endDate) {
      setWaterData(allWaterData);
      return;
    }

    const filteredData = allWaterData.filter(item => {
      const itemDate = new Date(item.date);

      if (startDate && !endDate) {
        return itemDate >= startDate;
      }

      if (!startDate && endDate) {
        return itemDate <= endDate;
      }

      return itemDate >= startDate && itemDate <= endDate;
    });

    setWaterData(filteredData);
  };

  const openAddDialog = () => {
    setIsEditMode(false);
    resetWaterForm();
    setWaterDialogVisible(true);
  };

  const openEditDialog = (item) => {
    setIsEditMode(true);
    setSelectedWaterItem(item);
    setWaterForm({
      sourceType: item.sourceType,
      openingReadingM3: item.openingReadingM3.toString(),
      closingReadingM3: item.closingReadingM3.toString(),
      consumptionM3: item.consumptionM3.toString(),
      date: new Date(item.date).toISOString().split('T')[0]
    });
    setWaterDialogVisible(true);
  };

  const handleWaterSave = async () => {
    if (!facilityId) {
      toastify("Facility ID is required", "error");
      return;
    }

    if (!waterForm.openingReadingM3 || !waterForm.closingReadingM3 || !waterForm.sourceType) {
      toastify("Please fill all required fields", "error");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...waterForm,
        openingReadingM3: parseFloat(waterForm.openingReadingM3),
        closingReadingM3: parseFloat(waterForm.closingReadingM3),
        consumptionM3: parseFloat(waterForm.consumptionM3)
      };

      let response;
      if (isEditMode && selectedWaterItem) {
        response = await makeRequest2(
          `${updateCommonAreaWaterReadingURL}/${facilityId}/${selectedWaterItem._id}`,
          "PUT",
          payload
        );
      } else {
        response = await makeRequest2(
          `${addCommonAreaWaterReadingUrl}/${facilityId}`,
          "POST",
          payload
        );
      }

      if (response.success) {
        toastify(`Water reading ${isEditMode ? 'updated' : 'added'} successfully`, "success");
        setWaterDialogVisible(false);
        resetWaterForm();
        fetchWaterData();
      } else {
        toastify(response.message || `Failed to ${isEditMode ? 'update' : 'add'} water reading`, "error");
      }
    } catch (error) {
      toastify(`Error ${isEditMode ? 'updating' : 'adding'} water reading`, "error");
    } finally {
      setLoading(false);
    }
  };

  const waterActionTemplate = (rowData) => {
    return (
      <div>
        <Button
          icon="ti ti-pencil"
          className="p-button-rounded p-button-info"
          onClick={() => openEditDialog(rowData)}
          tooltip="Edit Water Reading"
        />
      </div>
    );
  };

  const formatDate = (rowData) => {
    if (!rowData.date) return '';
    const date = new Date(rowData.date);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
  };

  const waterDialogFooter = (
    <div className="text-end mt-4">
      <Button
        type="button"
        className="btn btn-outline-secondary"
        onClick={() => setWaterDialogVisible(false)}
        disabled={loading}
      >
        Cancel
      </Button>
      &nbsp;
      <Button
        type="button"
        className="btn btn-primary"
        onClick={handleWaterSave}
        disabled={loading}
      >
        {loading ? "Saving..." : "Save"}
      </Button>
    </div>
  );

  return (
    <>
      <div className="mb-4">
        <div className="card">
          <div className="card-body d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <span className="me-2">Date Range:</span>
              <Calendar
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.value)}
                showIcon
                placeholder="Start Date"
                className="me-2"
              />
              <span className="me-2">to</span>
              <Calendar
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.value)}
                showIcon
                placeholder="End Date"
                className="me-2"
              />
              <Button
                icon="ti ti-search"
                className="ms-2"
                onClick={filterDataByDateRange}
                tooltip="Search"
              />
              <Button
                icon="ti ti-refresh"
                className="ms-2"
                onClick={() => {
                  setStartDate(null);
                  setEndDate(null);
                  setWaterData(allWaterData);
                }}
                tooltip="Reset"
              />
            </div>
            <button
              className="btn btn-primary"
              onClick={openAddDialog}
            >
              Add Water Reading
            </button>
          </div>
        </div>
      </div>
      <DataTable
        value={waterData}
        loading={loading}
        paginator
        rows={10}
        emptyMessage="No water data found"
        stripedRows
        sortMode="multiple"
        removableSort
        tableStyle={{ minWidth: '50rem' }}
      >
        <Column field="sourceType" header="Source Type" sortable />
        <Column field="openingReadingM3" header="Opening Reading (m³)" sortable />
        <Column field="closingReadingM3" header="Closing Reading (m³)" sortable />
        <Column field="consumptionM3" header="Consumption (m³)" sortable />
        <Column body={formatDate} header="Date" sortable field="date" />
        <Column header="Actions" body={waterActionTemplate} />
      </DataTable>

      {/* Water Dialog */}
      <Dialog
        visible={waterDialogVisible}
        style={{ width: '450px' }}
        header={isEditMode ? "Edit Water Reading" : "Add Water Reading"}
        modal
        className="p-fluid"
        footer={waterDialogFooter}
        onHide={() => setWaterDialogVisible(false)}
      >
        <div className="field">
          <label htmlFor="sourceType">Source Type</label>
          <Dropdown
            id="sourceType"
            value={waterForm.sourceType}
            options={waterSourceOptions}
            onChange={(e) => setWaterForm({ ...waterForm, sourceType: e.value })}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="openingReadingM3">Opening Reading (m³)</label>
          <InputText
            id="openingReadingM3"
            value={waterForm.openingReadingM3}
            onChange={(e) => {
              const newForm = { ...waterForm, openingReadingM3: e.target.value };
              setWaterForm(newForm);
              calculateWaterConsumption(e.target.value, waterForm.closingReadingM3, setWaterForm, newForm);
            }}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="closingReadingM3">Closing Reading (m³)</label>
          <InputText
            id="closingReadingM3"
            value={waterForm.closingReadingM3}
            onChange={(e) => {
              const newForm = { ...waterForm, closingReadingM3: e.target.value };
              setWaterForm(newForm);
              calculateWaterConsumption(waterForm.openingReadingM3, e.target.value, setWaterForm, newForm);
            }}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="consumptionM3">Consumption (m³)</label>
          <InputText
            id="consumptionM3"
            value={waterForm.consumptionM3}
            onChange={(e) => setWaterForm({ ...waterForm, consumptionM3: e.target.value })}
            required
            disabled={true}
          />
        </div>
        <div className="field">
          <label htmlFor="date">Date</label>
          <InputText
            id="date"
            type="date"
            value={waterForm.date}
            onChange={(e) => setWaterForm({ ...waterForm, date: e.target.value })}
            required
          />
        </div>
      </Dialog>
    </>
  );
};

export default WaterTab;